<script setup lang="ts">
import { ref, watchEffect } from "vue";
import { isActive as isOfficeActive } from "../store/Office";
import tinycolor from "tinycolor2";

const dark = ref(false);

dark.value =
  localStorage.theme === "dark" ||
  (!("theme" in localStorage) &&
    window.matchMedia("(prefers-color-scheme: dark)").matches);

watchEffect(() => {
  if (dark.value) document.documentElement.classList.add("dark");
  else document.documentElement.classList.remove("dark");
});

watchEffect(() => {
  if (isOfficeActive.value) {
    const color = new tinycolor(Office.context.officeTheme.bodyBackgroundColor);
    dark.value = color.isDark();
  }
});

const toggleDark = () => {
  dark.value = !dark.value;
  localStorage.theme = dark.value ? 'dark' : 'light';
};
</script>

<template>
  <button
    @click="toggleDark"
    type="button"
    aria-label="Basculer le mode sombre"
    class="
      group
      relative
      flex
      items-center
      justify-center
      w-10
      h-10
      text-gray-600
      dark:text-gray-300
      bg-white
      dark:bg-gray-800
      border-2
      border-gray-200
      dark:border-gray-700
      rounded-lg
      hover:border-primary-400
      dark:hover:border-primary-600
      transition-all
      duration-300
      shadow-md
      hover:shadow-lg
      focus-ring
      overflow-hidden
    "
  >
    <!-- Effet de fond au survol -->
    <div class="
        absolute
        inset-0
        bg-primary-50
        dark:bg-primary-900/20
        opacity-0
        group-hover:opacity-100
        transition-opacity
        duration-300
      "></div>

    <!-- Icône Soleil (mode sombre actif) -->
    <svg
      v-if="dark"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      class="
        relative
        z-10
        w-5
        h-5
        text-yellow-500
        dark:text-yellow-400
        group-hover:scale-110
        group-hover:rotate-90
        transition-all
        duration-300
      "
    >
      <path
        d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
        fill-rule="evenodd"
        clip-rule="evenodd"
      ></path>
    </svg>

    <!-- Icône Lune (mode clair actif) -->
    <svg
      v-else
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      class="
        relative
        z-10
        w-5
        h-5
        text-indigo-600
        dark:text-indigo-400
        group-hover:scale-110
        group-hover:-rotate-12
        transition-all
        duration-300
      "
    >
      <path
        d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"
      ></path>
    </svg>

    <!-- Tooltip -->
    <span class="
        absolute
        -bottom-10
        left-1/2
        transform
        -translate-x-1/2
        px-2
        py-1
        bg-gray-900
        dark:bg-gray-700
        text-white
        text-xs
        rounded
        opacity-0
        group-hover:opacity-100
        transition-opacity
        duration-200
        pointer-events-none
        whitespace-nowrap
      ">
      {{ dark ? 'Mode clair' : 'Mode sombre' }}
    </span>
  </button>
</template>
